import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import type { Ticket } from "@/services/ticketsService";
import { STATUSES, TEAM_DOT, TEAM_STYLE, PRIO_STYLE, statusMeta } from "./ticketConstants";

export function TicketCard({
  ticket,
  showTenant,
  onOpen,
}: {
  ticket: Ticket;
  showTenant: boolean;
  onOpen: () => void;
}) {
  // statusMeta() falls back to STATUSES[0] ("Not started") for an
  // unrecognized/empty status — fine for a single Table cell, but this badge
  // is the primary status signal on the card, so an unknown status must
  // render as nothing rather than a wrong-but-plausible "Not started".
  const status = STATUSES.some((s) => s.key === ticket.status) ? statusMeta(ticket.status) : null;
  return (
    <button
      type="button"
      onClick={onOpen}
      className="w-full rounded-lg border border-border bg-card p-3 text-left shadow-sm transition-colors hover:border-border/80 hover:shadow"
    >
      {/* flex-wrap, not overflow: visible (the pre-existing default). A
          narrow Kanban column plus team + status + tenant (cross-tenant
          view) + priority overflows the card's own width — with no wrap,
          that overflow doesn't clip or scroll, it bleeds visibly into the
          NEXT column's cards. Confirmed with a long tenant_id + high
          priority: content rendered ~85px past the card's right edge.
          Wrapping to a second line keeps every badge inside the card. */}
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className={cn("gap-1.5", TEAM_STYLE[ticket.category])}>
          <span className={cn("h-1.5 w-1.5 rounded-full", TEAM_DOT[ticket.category] ?? "bg-muted-foreground")} />
          {ticket.team}
        </Badge>
        {/* Kanban implies status via column, List via group heading — but a
            card can be screenshotted, dragged mid-view, or just glanced at
            out of that context, so the status is repeated here too rather
            than assuming the surrounding layout always carries it. */}
        {status ? (
          <Badge variant="outline" className={cn("gap-1.5", status.style)}>
            <span className={cn("h-1.5 w-1.5 rounded-full", status.dot)} />
            {status.label}
          </Badge>
        ) : null}
        {showTenant ? (
          <Badge variant="outline" className="ml-1 truncate text-[10px] text-muted-foreground">
            {ticket.tenant_id}
          </Badge>
        ) : null}
        <span className={cn("ml-auto text-xs font-semibold", PRIO_STYLE[ticket.priority])}>
          {ticket.priority[0].toUpperCase() + ticket.priority.slice(1)}
        </span>
      </div>
      <p className="mt-2 line-clamp-2 text-sm font-medium leading-snug text-foreground">
        {ticket.title}
      </p>
      <div className="mt-2 flex items-center gap-2 text-xs text-muted-foreground">
        <span className="truncate">{ticket.requested_by || ticket.tenant_id}</span>
        {ticket.assignee_name ? <span className="ml-auto">{ticket.assignee_name}</span> : null}
      </div>
    </button>
  );
}
